import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import styles from "../modals/ResourceViewerModal.module.css";
import { resolveName } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";
import { calculateAge } from "../utils/dateUtils";
import { useStore } from "react-redux";
import { formatLocalDate } from "../utils/dateUtils";
import { useResolveReferences } from "../utils/resolveReferences";


const preloadImage = (url, fallback) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => resolve(fallback);
    img.src = url;
  });
};

const EncounterCardExtended = ({ resource, patientData, providerData, handleShowNested, fhirClient }) => {
  // --- State for images ---
  const [patientImgSrc, setPatientImgSrc] = useState(null);
  const [providerImgSrc, setProviderImgSrc] = useState(null);
  const [patientLoading, setPatientLoading] = useState(true);
  const [providerLoading, setProviderLoading] = useState(true);
  const store = useStore();
  useResolveReferences(resource, fhirClient, store);


  // --- Load images with fallback ---
  useEffect(() => {
    const loadPatientImage = async () => {
      if (!patientData) {
        setPatientImgSrc(FemalePatient);
        setPatientLoading(false);
        return;
      }
      const fallback = (patientData.gender || "").toLowerCase() === "female" ? FemalePatient : MalePatient;
      const url = patientData.photo?.[0]?.url || fallback;
      const loadedUrl = await preloadImage(url, fallback);
      setPatientImgSrc(loadedUrl);
      setPatientLoading(false);
    };

    const loadProviderImage = async () => {
      if (!providerData) {
        setProviderImgSrc(MaleProvider);
        setProviderLoading(false);
        return;
      }
      const fallback = (providerData.gender || "").toLowerCase() === "female" ? FemaleProvider : MaleProvider;
      const url = providerData.photo?.[0]?.url || fallback;
      const loadedUrl = await preloadImage(url, fallback);
      setProviderImgSrc(loadedUrl);
      setProviderLoading(false);
    };

    loadPatientImage();
    loadProviderImage();
  }, [patientData, providerData]);

  // --- Names ---
  const patientFullName = patientData?.name?.[0]
    ? `${(patientData.name[0].given || []).join(" ")} ${patientData.name[0].family || ""}`.trim()
    : "Unknown";

  const providerFullName = providerData?.name?.[0]
    ? `${(providerData.name[0].prefix || []).join(" ")} ${(providerData.name[0].given || []).join(" ")} ${providerData.name[0].family || ""}`.trim()
    : "Unknown";

  const patientAge = calculateAge(patientData?.birthDate);


  const renderIdentifier = (identifier) => {
    if (!identifier) return null;
    const parts = [];
    if (identifier.system) parts.push(`System: ${identifier.system}`);
    if (identifier.value) parts.push(`Value: ${identifier.value}`);
    if (identifier.type?.text) parts.push(`Type: ${identifier.type.text}`);
    if (identifier.use) parts.push(`Use: ${identifier.use}`);
    if (identifier.assigner?.display) parts.push(`Assigner: ${identifier.assigner.display}`);
    return <div className="mb-1">{parts.join(" • ")}</div>;
  };

  const renderIdentifiers = (ids) => {
    if (!ids || ids.length === 0) return <div className="text-muted">N/A</div>;
    return ids.map((id, idx) => <div key={idx}>{renderIdentifier(id)}</div>);
  };

  const renderCoding = (coding) => {
    if (!coding) return null;
    const parts = [];
    if (coding.system) parts.push(coding.system);
    if (coding.code) parts.push(coding.code);
    if (coding.display) parts.push(`"${coding.display}"`);
    return <div className="mb-1">{parts.join(" — ")}</div>;
  };

  const renderCodeableConcept = (cc) => {
    if (!cc) return <span className="text-muted">N/A</span>;
    const display = cc.text || (cc.coding && cc.coding[0]?.display) || cc.coding?.map((c) => c.code).join(", ") || "CodeableConcept";
    return (
      <div>
        <div><strong>{display}</strong></div>
        {cc.coding && cc.coding.length > 0 && (
          <div className="small text-muted">
            {cc.coding.map((c, i) => (
              <div key={i}>{renderCoding(c)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPeriod = (period) => {
    if (!period) return <span className="text-muted">N/A</span>;

    return (
      <div>
        <div>Start: {period.start ? formatLocalDate(period.start) : "N/A"}</div>
        <div>End: {period.end ? formatLocalDate(period.end) : "N/A"}</div>
      </div>
    );
  };
  const renderQuantity = (q) => {
    if (!q) return <span className="text-muted">N/A</span>;
    return `${q.value || ""} ${q.unit || q.code || ""}`.trim();
  };


  const renderLocationEntry = (loc, idx) => {
    return (
      <div key={idx} className="mb-2">
        <div><strong>Location:</strong> {renderReference(loc.location?.reference, loc.location?.display || loc.location?.reference, handleShowNested)}</div>
        <div><strong>Status:</strong> {loc.status || <span className="text-muted">N/A</span>}</div>
        <div><strong>Physical Type:</strong> {renderCodeableConcept(loc.physicalType)}</div>
        <div><strong>Period:</strong> {renderPeriod(loc.period)}</div>
      </div>
    );
  };

  const renderDiagnosis = (d, idx) => {
    // Use reference only if it exists, otherwise fallback to "N/A"
    const condRef = d.condition?.reference || null;
    const condDisplay = d.condition?.display || condRef || "N/A";
    const useCoding = d.use?.coding?.[0] || {};
    const useSystem = useCoding.system || "N/A";
    const useCode = useCoding.code || "N/A";
    const useDisplay = useCoding.display || "N/A";
    const rank = d.rank || "N/A";

    return (
      <tr key={idx}>
        <td>{condRef ? renderReference(condRef, condDisplay, handleShowNested) : condDisplay}</td>
        <td>{useSystem}</td>
        <td>{useCode}</td>
        <td>{useDisplay}</td>
        <td>{rank}</td>
      </tr>
    );
  };


  // --- Safely get arrays or default ---
  const identifiers = resource.identifier || [];
  const statusHistory = resource.statusHistory || [];
  const classHistory = resource.classHistory || [];
  const types = resource.type || [];
  const priority = resource.priority;
  const serviceType = resource.serviceType;
  const serviceCategory = resource.serviceType || resource.serviceCategory || null;
  const basedOn = resource.basedOn || [];
  const episodeOfCare = resource.episodeOfCare || [];
  const participants = resource.participant || [];
  const locations = resource.location || [];
  const reasonCode = resource.reasonCode || [];
  const reasonReference = resource.reasonReference || [];
  const diagnoses = resource.diagnosis || [];
  const accounts = resource.account || [];
  const hospitalization = resource.hospitalization || {};
  const status = resource.status || "Unknown";
  const encounterClass = resource.class || {};
  const period = resource.period || {};
  const length = resource.length;
  const appointment = resource.appointment;
  const partOf = resource.partOf;
  const serviceProvider = resource.serviceProvider;
  const text = resource.text?.div ? resource.text.div : null;
  const identifierFirst = identifiers[0];

  // --- Render main ---
  return (
    <div className={styles.card}>
      <div className="d-flex justify-content-between mb-4">
        {/* Patient Section */}
        <div className="d-flex align-items-center">
          <button
            onClick={() => handleShowNested?.(patientData, patientFullName)}
            style={{ padding: 0, border: "none", background: "none", cursor: "pointer", marginRight: "1rem" }}
          >
            <div style={{ position: "relative" }}>
              {patientLoading && <Spinner animation="border" />}
              {patientImgSrc && (
                <img
                  src={patientImgSrc}
                  alt="Patient Portrait"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    backgroundColor: "#f8f9fa",
                    display: patientLoading ? "none" : "block",
                  }}
                />
              )}
            </div>
          </button>
          <div>
            <div className="mb-1 text-muted"><strong>Patient:</strong> {patientFullName}</div>
            <div className="mb-1 text-muted"><strong>Gender:</strong> {patientData?.gender || "Unknown"}</div>
            <div className="mb-1 text-muted"><strong>Birth Date:</strong> {patientData?.birthDate || "Unknown"}</div>
            <div className="mb-1 text-muted"><strong>Age:</strong> {patientAge}</div>
          </div>
        </div>

        {/* Provider Section */}
        <div className="d-flex align-items-center">
          {providerData ? (
            <>
              <button
                onClick={() => handleShowNested?.(providerData, providerFullName)}
                style={{ padding: 0, border: "none", background: "none", cursor: "pointer", marginRight: "1rem" }}
              >
                <div style={{ position: "relative" }}>
                  {providerLoading && <Spinner animation="border" />}
                  {providerImgSrc && (
                    <img
                      src={providerImgSrc}
                      alt="Provider Portrait"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        backgroundColor: "#f8f9fa",
                        display: providerLoading ? "none" : "block",
                      }}
                    />
                  )}
                </div>
              </button>
              <div>
                <div className="mb-1 text-muted"><strong>Provider:</strong> {providerFullName}</div>
                <div className="mb-1 text-muted"><strong>Gender:</strong> {providerData?.gender || "Unknown"}</div>
                <div className="mb-1 text-muted"><strong>Active:</strong> {providerData?.active ? "Yes" : "No"}</div>
              </div>
            </>
          ) : (
            <div className="text-muted"><strong>No Provider associated with this encounter.</strong></div>
          )}
        </div>
      </div>


      {/* Encounter Header */}
      <div className={styles.sectionHeader}>Encounter Summary</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {/* ID / Status / Class on one line */}
          <div className="d-flex gap-3 mb-1 flex-wrap">
            <div><strong>Encounter ID:</strong> {resource.id || (identifierFirst?.value ? identifierFirst.value : "Unknown")}</div>
            <div><strong>Status:</strong> {status}</div>
            <div>
              <strong>Class:</strong> {encounterClass?.code ? `${encounterClass.code} ${encounterClass.display ? `(${encounterClass.display})` : ""}` : <span className="text-muted">N/A</span>}
            </div>
          </div>

          {/* Period start/end and length on same line */}
          <div className="d-flex gap-3 mb-1 flex-wrap">
            <div>
              <strong>Period:</strong>{" "}
              {formatLocalDate(period.start) ?? "N/A"} — {formatLocalDate(period.end) ?? "N/A"}

            </div>
            <div>
              <strong>Length:</strong> {length ? renderQuantity(length) : <span className="text-muted">N/A</span>}
            </div>
          </div>

          {/* Appointment */}
          <div className="mb-1">
            <strong>Appointment:</strong>{" "}
            {appointment ? (
              Array.isArray(appointment)
                ? appointment.map((a, i) => renderReference(a.reference || a, a.display || a.reference || `Appointment ${i + 1}`, handleShowNested))
                : renderReference(appointment.reference, appointment.display || appointment.reference, handleShowNested)
            ) : (
              <span className="text-muted">N/A</span>
            )}
          </div>
        </div>
      </div>


      {/* Identifiers */}
      <div className={styles.sectionHeader}>Identifiers</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {identifiers.length ? renderIdentifiers(identifiers) : <div className="text-muted">N/A</div>}
        </div></div>


      <div className={styles.sectionHeader}>Participants</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {participants?.length ? (
            participants.map((p, idx) => {
              //console.log(p);
              // Support both Encounter.participant (p.individual) and direct Reference-like objects
              const ref = p?.individual?.reference ?? p?.individual ?? p?.reference ?? p;
              if (!ref) {
                return (
                  <span key={idx} className="text-muted">
                    N/A
                  </span>
                );
              }
              const displayName = resolveName(ref, store);
              return (
                <button
                  key={idx}
                  className="btn btn-link p-0"
                  onClick={() =>
                    handleShowNested?.(ref, displayName)
                  }
                >
                  {displayName}
                </button>
              );
            })
          ) : (
            <span className="text-muted">N/A</span>
          )}

        </div></div>




      {/* Reasons / Diagnoses */}
      <div className={styles.sectionHeader}>Reasons / Diagnoses</div>
      <div className={styles.indented}>
        <div className="mb-2">
          <strong>Reason Code(s):</strong>
          {reasonCode.length ? reasonCode.map((rc, i) => <div key={i} className="ps-3">{renderCodeableConcept(rc)}</div>) : <div className="text-muted ps-3">N/A</div>}
        </div>

        <div className="mb-2">
          <strong>Reason Reference(s):</strong>
          {reasonReference.length ? reasonReference.map((rr, i) => <div key={i} className="ps-3">{renderReference(rr.reference || rr, rr.display || rr.reference, handleShowNested)}</div>) : <div className="text-muted ps-3">N/A</div>}
        </div>

        <div className={styles.sectionHeader}>Diagnosis</div>
        <div className="pt-2">
          {diagnoses.length ? (
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Condition</th>
                  <th>Use System</th>
                  <th>Use Code</th>
                  <th>Use Display</th>
                  <th>Rank</th>
                </tr>
              </thead>
              <tbody>
                {diagnoses.map((d, i) => renderDiagnosis(d, i))}
              </tbody>
            </table>
          ) : (
            <div className="text-muted">N/A</div>
          )}
        </div>
      </div>

      {/* Status / Class History */}
      <div className={styles.sectionHeader}>Status & Class History</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>

          <div className="mb-2">
            <strong>Status History:</strong>
            {statusHistory.length ? (
              statusHistory.map((sh, i) => (
                <div key={i} className="ps-3">
                  <div><strong>Status:</strong> {sh.status || "N/A"}</div>
                  <div><strong>Period:</strong> {renderPeriod(sh.period)}</div>
                </div>
              ))
            ) : (
              <div className="text-muted ps-3">N/A</div>
            )}
          </div>

          <div className="mb-2">
            <strong>Class History:</strong>
            {classHistory.length ? (
              classHistory.map((ch, i) => (
                <div key={i} className="ps-3">
                  <div><strong>Class:</strong> {ch.class?.code || ch.class?.display || "N/A"}</div>
                  <div>
                    <strong>Period:</strong>{" "}
                    {formatLocalDate(ch.period?.start)} — {formatLocalDate(ch.period?.end)}
                  </div>

                </div>
              ))
            ) : (
              <div className="text-muted ps-3">N/A</div>
            )}
          </div>
        </div></div>

      {/* Types / Service */}
      <div className={styles.sectionHeader}>Type / Service</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <div className="mb-2"><strong>Type(s):</strong>{types.length ? types.map((t, i) => <div key={i} className="ps-3">{renderCodeableConcept(t)}</div>) : <div className="text-muted ps-3">N/A</div>}</div>

          <div className="mb-2"><strong>Priority:</strong> {priority ? renderCodeableConcept(priority) : <span className="text-muted">N/A</span>}</div>

          <div className="mb-2"><strong>Service Type:</strong> {serviceType ? renderCodeableConcept(serviceType) : <span className="text-muted">N/A</span>}</div>

          <div className="mb-2"><strong>Service Category:</strong> {serviceCategory ? renderCodeableConcept(serviceCategory) : <span className="text-muted">N/A</span>}</div>
        </div></div>

      {/* BasedOn / EpisodeOfCare / PartOf */}
      <div className={styles.sectionHeader}>Context & Links</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <div className="mb-1">
            <strong>Based On:</strong>
            {basedOn.length ? basedOn.map((b, i) => <div key={i} className="ps-3">{renderReference(b.reference || b, b.display || b.reference, handleShowNested)}</div>) : <span className="text-muted">N/A</span>}
          </div>

          <div className="mb-1">
            <strong>Episode Of Care:</strong>
            {episodeOfCare.length ? episodeOfCare.map((e, i) => <div key={i} className="ps-3">{renderReference(e.reference || e, e.display || e.reference, handleShowNested)}</div>) : <span className="text-muted">N/A</span>}
          </div>

          <div className="mb-1"><strong>Part Of:</strong> {partOf ? renderReference(partOf.reference || partOf, partOf.display || partOf.reference || partOf, handleShowNested) : <span className="text-muted">N/A</span>}</div>
        </div></div>

      {/* Hospitalization */}
      <div className={styles.sectionHeader}>Hospitalization</div>
      <div className={styles.indented}>
         <div className={styles.sectionContent}>
        <div className="mb-1"><strong>Pre-admission Identifier:</strong> {hospitalization.preAdmissionIdentifier ? renderIdentifier(hospitalization.preAdmissionIdentifier) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Origin:</strong> {hospitalization.origin ? renderReference(hospitalization.origin.reference, hospitalization.origin.display || hospitalization.origin.reference, handleShowNested) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Admit Source:</strong> {hospitalization.admitSource ? renderCodeableConcept(hospitalization.admitSource) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Re-admission:</strong> {hospitalization.reAdmission ? renderCodeableConcept(hospitalization.reAdmission) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Diet Preference(s):</strong> {hospitalization.dietPreference && hospitalization.dietPreference.length ? hospitalization.dietPreference.map((d, i) => (<div key={i}>{renderCodeableConcept(d)}</div>)) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Special Courtesy:</strong> {hospitalization.specialCourtesy && hospitalization.specialCourtesy.length ? hospitalization.specialCourtesy.map((c, i) => (<div key={i}>{renderCodeableConcept(c)}</div>)) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Special Arrangement:</strong> {hospitalization.specialArrangement && hospitalization.specialArrangement.length ? hospitalization.specialArrangement.map((a, i) => (<div key={i}>{renderCodeableConcept(a)}</div>)) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Destination:</strong> {hospitalization.destination ? renderReference(hospitalization.destination.reference, hospitalization.destination.display || hospitalization.destination.reference, handleShowNested) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Discharge Disposition:</strong> {hospitalization.dischargeDisposition ? renderCodeableConcept(hospitalization.dischargeDisposition) : <span className="text-muted">N/A</span>}</div>
      </div></div>

      {/* Location(s) */}
      <div className={styles.sectionHeader}>Location(s)</div>
      <div className={styles.indented}>
          <div className={styles.sectionContent}>
        {locations.length ? locations.map((l, i) => renderLocationEntry(l, i)) : <div className="text-muted">N/A</div>}
      </div></div>

      {/* Accounts */}
      <div className={styles.sectionHeader}>Account(s)</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
        {accounts.length ? accounts.map((a, i) => <div key={i}>{renderReference(a.reference || a, a.display || a.reference || `Account ${i + 1}`, handleShowNested)}</div>) : <div className="text-muted">N/A</div>}
      </div></div>

      {/* Service Provider & Other Links */}
      <div className={styles.sectionHeader}>Service Provider & Other Links</div>
      <div className={styles.indented}>
          <div className={styles.sectionContent}>
        <div className="mb-1"><strong>Service Provider:</strong> {serviceProvider ? renderReference(serviceProvider.reference || serviceProvider, resolveName(serviceProvider.reference, store) || serviceProvider.reference || serviceProvider, handleShowNested) : <span className="text-muted">N/A</span>}</div>
        <div className="mb-1"><strong>Subject (Patient):</strong> {renderReference(resource.subject?.reference, patientFullName || resource.subject?.display, handleShowNested)}</div>
        <div className="mb-1"><strong>Entered In Error:</strong> {resource.enteredInError ? resource.enteredInError : "N/A"}</div>
      </div></div>

      {/* Raw text / Notes / Extensions */}
      <div className={styles.sectionHeader}>Raw / Extras</div>
      <div className={styles.indented}>
        <div className="mb-2"><strong>Text:</strong> {text ? <div dangerouslySetInnerHTML={{ __html: text }} /> : <span className="text-muted">N/A</span>}</div>

        <div className="mb-2"><strong>Extensions:</strong> {resource.extension && resource.extension.length ? resource.extension.map((ext, i) => (
          <div key={i} className="ps-3">
            <div><strong>URL:</strong> {ext.url}</div>
            <div><strong>Value:</strong> {ext.valueString || ext.valueCode || ext.valueReference?.reference || JSON.stringify(ext).slice(0, 150)}</div>
          </div>
        )) : <span className="text-muted">N/A</span>}</div>

        <div className="mb-2"><strong>Contained Resources Count:</strong> {resource.contained ? resource.contained.length : 0}</div>
      </div>
    </div>
  );
};

export default EncounterCardExtended;
